import React from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

interface ProcessStep {
  number: string;
  title: string;
  description: string;
  icon: string;
  timeline: string;
}

const ProcessOverview = () => {
  const steps: ProcessStep[] = [
    {
      number: '01',
      title: 'Submit Fleet Details',
      description: 'Provide basic information about your vehicles through our simple online form or speak directly with a specialist.',
      icon: 'DocumentTextIcon',
      timeline: '5 Minutes'
    },
    {
      number: '02',
      title: 'Receive Valuation',
      description: 'Get a detailed, transparent valuation report within 24-48 hours showing exactly how we calculated your fleet\'s worth.',
      icon: 'CalculatorIcon',
      timeline: '24-48 Hours'
    },
    {
      number: '03',
      title: 'Review & Accept',
      description: 'Take time to review our offer with no pressure. Ask questions, request clarifications, and make an informed decision.',
      icon: 'ClipboardDocumentCheckIcon',
      timeline: 'Your Timeline'
    },
    {
      number: '04',
      title: 'We Handle Everything',
      description: 'Once accepted, we manage all paperwork, arrange pickup, process titles, and transfer funds securely to your account.',
      icon: 'CheckBadgeIcon',
      timeline: '3-5 Days'
    }
  ];

  return (
    <section className="bg-card py-16 md:py-20">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            Simple, Transparent Process
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            From initial inquiry to final payment, we've streamlined every step to make your fleet sale effortless.
          </p>
        </div>

        {/* Process Steps */}
        <div className="relative">
          {/* Connection Line - Desktop */}
          <div className="hidden lg:block absolute top-24 left-0 right-0 h-0.5 bg-border" style={{ left: '12.5%', right: '12.5%' }} />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, index) => (
              <div key={index} className="relative">
                {/* Step Card */}
                <div className="bg-background rounded-xl p-6 hover:shadow-brand transition-all duration-200 border border-border h-full">
                  {/* Step Number */}
                  <div className="relative mb-6">
                    <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto relative z-10">
                      <span className="text-2xl font-bold text-primary-foreground">{step.number}</span>
                    </div>
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-20 h-20 bg-primary/20 rounded-full" />
                  </div>

                  {/* Icon */}
                  <div className="flex justify-center mb-4">
                    <div className="p-3 bg-primary/10 rounded-lg">
                      <Icon name={step.icon as any} size={32} variant="outline" className="text-primary" />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="text-center space-y-3">
                    <h3 className="text-xl font-bold text-foreground">{step.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {step.description}
                    </p>
                    <div className="pt-2">
                      <span className="inline-flex items-center space-x-2 text-xs font-semibold text-accent bg-accent/10 px-3 py-1 rounded-full">
                        <Icon name="ClockIcon" size={14} variant="outline" />
                        <span>{step.timeline}</span>
                      </span>
                    </div>
                  </div>
                </div>

                {/* Arrow - Mobile/Tablet */}
                {index < steps.length - 1 && (
                  <div className="lg:hidden flex justify-center my-4">
                    <Icon name="ArrowDownIcon" size={24} variant="outline" className="text-primary" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="mt-12 text-center">
          <div className="bg-primary/5 rounded-xl p-8 border border-primary/10">
            <h3 className="text-xl font-bold text-foreground mb-3">
              Ready to Get Started?
            </h3>
            <p className="text-base text-muted-foreground mb-6 max-w-2xl mx-auto">
              The entire process typically takes less than a week from initial contact to payment. Let's begin your fleet valuation today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                href="/contact"
                className="px-8 py-4 bg-cta text-cta-foreground font-semibold rounded-lg hover:bg-opacity-90 transition-all duration-200 ease-out hover:-translate-y-1 shadow-brand inline-flex items-center justify-center space-x-2"
              >
                <span>Start Your Valuation</span>
                <Icon name="ArrowRightIcon" size={20} variant="outline" />
              </Link>
              <Link
                href="/fleet-evaluation-process"
                className="px-8 py-4 bg-card text-foreground font-semibold rounded-lg hover:bg-muted transition-all duration-200 ease-out border border-border inline-flex items-center justify-center space-x-2"
              >
                <span>Learn More About Our Process</span>
                <Icon name="InformationCircleIcon" size={20} variant="outline" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProcessOverview;